import {client} from '@core/GraphQLProvider';
import {createQueueManager} from '@core/GraphQLProvider/util';
import appLogger from '@core/LoggingProvider';
import {
    AddLineItems,
    AddPromotion,
    BookOrder,
    DeleteLineItems,
    GetAvailableShippingMethods,
    GetCart,
    GetCartStatus,
    PriceCart,
    RemovePromotion,
    SetCartStatus,
    UpdateLineItems,
    UpdateShippingAddress,
    UpdateShippingMethod
} from './query.graphql';
import dataLayer from '../../upi/data-layer';
import {
    applyProductSelectionAttribution,
    rememberCartAttributions,
    removeCartAttributions,
    removeProductSelections
} from '../../upi/commerce-attribution-store';
import {
    Cart,
    cartAdditionsStrategy,
    cartDeletionsStrategy,
    cartUpdatesStrategy,
    mergeCart,
    toCartEntity,
    toLineItemEntity,
    toLineItemInput,
    toLineItemsPayload
} from '@modules/cart/Cart';
import {isEmptyOrNil} from '@ultradent/utilities/EmptyOrNil';

const ADD_LINE_ITEMS = 'addLineItems';
const UPDATE_LINE_ITEMS = 'updateLineItems';
const DELETE_LINE_ITEMS = 'deleteLineItems';
const DISABLE_TIMEOUT = -1;
const mutationsQueue = createQueueManager( [ADD_LINE_ITEMS, UPDATE_LINE_ITEMS, DELETE_LINE_ITEMS] );
const eventMap = {
    [ADD_LINE_ITEMS]: 'lineItemsAdded',
    [UPDATE_LINE_ITEMS]: 'lineItemsUpdated',
    [DELETE_LINE_ITEMS]: 'lineItemsDeleted'
};

const addPromotion = async ( code ) => {
    if ( !code ) {
        throw new Error( `"code" is a required parameter, received "${code}" instead` );
    }

    const resp = await client.mutate( {
        mutation: AddPromotion,
        variables: {input: code}
    } );

    return resp.data.addPromotion;
}

const removePromotion = async ( code ) => {
    if ( !code ) {
        throw new Error( `"code" is a required parameter, received "${code}" instead` );
    }

    const resp = await client.mutate( {
        mutation: RemovePromotion,
        variables: {input: code}
    } );

    return resp.data.removePromotion;
}

const updateShippingMethod = async ( shippingMethod ) => {
    if ( !shippingMethod ) {
        throw new Error( `"shippingMethod" is a required parameter, received "${shippingMethod}" instead` );
    }
    const resp = await client.mutate( {
        mutation: UpdateShippingMethod,
        variables: {input: shippingMethod}
    } );

    return resp.data;
}

const updateShippingAddress = async ( shippingAddressId ) => {
    if ( !shippingAddressId ) {
        throw new Error( `"shippingAddressId" is a required parameter, received "${shippingAddressId}" instead` );
    }
    const resp = await client.mutate( {
        mutation: UpdateShippingAddress,
        variables: {input: shippingAddressId}
    } );

    return resp.data;
}

const getAvailableShippingMethods = async () => {
    const resp = await client.query( {
        fetchPolicy: 'network-only',
        query: GetAvailableShippingMethods
    } );

    return resp.data.getShippingMethods;
};

const getLineItemSku = lineItem => lineItem?.product?.sku;

const getConfirmedAdditions = ( submittedLineItems, changedLineItems ) => {
    if ( !Array.isArray( changedLineItems ) ) {
        return submittedLineItems;
    }

    const submittedBySku = submittedLineItems.reduce( ( lineItemsBySku, lineItem ) => {
        const sku = getLineItemSku( lineItem );
        if ( sku ) {
            lineItemsBySku[sku.toLowerCase()] = lineItem;
        }
        return lineItemsBySku;
    }, {} );
    const confirmedLineItems = changedLineItems
        .filter( lineItem => {
            const sku = getLineItemSku( lineItem );
            return sku && submittedBySku[sku.toLowerCase()] && Number( lineItem.quantity ) > 0;
        } )
        .map( lineItem => {
            const submittedLineItem = submittedBySku[getLineItemSku( lineItem ).toLowerCase()];
            return {
                ...submittedLineItem,
                ...lineItem,
                product: {
                    ...(submittedLineItem.product || {}),
                    ...(lineItem.product || {})
                },
                itemList: lineItem.itemList || submittedLineItem.itemList
            };
        } );

    return confirmedLineItems;
};

const getCart = async () => {
    const resp = await client.query( {
        query: GetCart
    } );

    return resp.data.cart;
};

const priceCart = async () => {
    const resp = await client.mutate( {
        mutation: PriceCart
    } );

    return resp.data.priceCart;
};

export const readCartFromCache = ( {optimistic = false} = {} ) => {
    try {
        const {cart} = client.cache.readQuery( {query: GetCart}, optimistic );
        return cart;
    }
    catch ( err ) {
        return null;
    }
};

export const getCartFromCache = ( options = {} ) => {
    const cart = readCartFromCache( options );

    // ensure we have a cart in the local cache
    if ( !cart ) {
        return seedEmptyCart().cart;
    }

    return cart;
};

const getCartStatus = () => {
    return client.cache.readQuery( {query: GetCartStatus} );
};
const subscribeCartStatus = callback => client.cache.watch( {
    query: GetCartStatus,
    optimistic: true,
    callback: ( {result} ) => callback( result )
} );
const setCartStatus = ( input ) => {
    return client.mutate( {
        mutation: SetCartStatus,
        variables: {input},
        update: (( cache, resp ) => {
            const {cartStatus} = client.cache.readQuery( {query: GetCartStatus} );
            console.warn( '[Client:CartService.setCartStatus] cartStatus', cartStatus );
        })
    } );
};

const seedEmptyCart = () => {
    const data = {cart: toCartEntity()};
    client.cache.writeQuery( {
        query: GetCart,
        data
    } );

    return data;
};

export const clearCartCache = () => seedEmptyCart().cart;

const mergeLineItems = ( cachedLineItems = [], mutationLineItems = [], fallbackLineItems = [] ) => {
    if ( !Array.isArray( mutationLineItems ) ) {
        return cachedLineItems;
    }

    return mutationLineItems.map( lineItem => {
        const cachedLineItem = cachedLineItems.find(
            item => item.product && lineItem.product && item.product.sku === lineItem.product.sku
        ) || fallbackLineItems.find(
            item => item.product && lineItem.product && item.product.sku === lineItem.product.sku
        ) || {};

        return {
            ...cachedLineItem,
            ...lineItem,
            product: {
                ...(cachedLineItem.product || {}),
                ...(lineItem.product || {})
            }
        };
    } );
};

const syncCart = ( mutationName, query, fallbackLineItems ) => ( proxy, {data} ) => {
    const mutationCart = data && data[mutationName] && data[mutationName].cart;

    if ( !mutationCart ) {
        return;
    }

    let state = {cart: {}};
    try {
        state = proxy.readQuery( {query} );
    }
    catch ( err ) {}

    const dataUpdated = {
        cart: {
            ...state.cart,
            ...mutationCart,
            lineItems: mergeLineItems( state.cart.lineItems, mutationCart.lineItems, fallbackLineItems )
        }
    };

    proxy.writeQuery( {query, data: dataUpdated} );
};

const trackCartChange = ( changeType, lineItems ) => {
    if ( !Array.isArray( lineItems ) || lineItems.length === 0 ) {
        return;
    }

    const productData = lineItems.map( line => {
        return {
            ...line.product,
            quantity: line.quantity,
            linePrice: line.linePrice,
            currencyCode: line.currencyCode,
            currencyCountry: line.currencyCountry,
            itemList: line.itemList
        };
    } );

    switch ( changeType ) {
        case ADD_LINE_ITEMS:
            dataLayer.cart.sendItemsAdded( productData );
            return;
        case DELETE_LINE_ITEMS:
            return dataLayer.cart.sendItemsRemoved( productData );
        default:
            console.warn( `Ignoring unknown tracking type "${changeType}"` );
    }
};

const runCartSideEffect = ( name, effect, fallback ) => {
    try {
        return effect();
    }
    catch ( err ) {
        console.warn( `[CartService:${name}]`, err );
        appLogger.capture( err );
        return fallback;
    }
};

const createLineItemMutation = ( mutation, selection, mergeStrategy, propagateErrors = false ) =>
    async ( lineItems, options = {} ) => {

        if ( !Array.isArray( lineItems ) ) {
            lineItems = [lineItems];
        }

        // consecutive mutations need to be tracked in a queue to build accurate optimisticResponse object
        const queuedLineItemMutations = mutationsQueue.add(
            selection,
            lineItems.map( toLineItemEntity )
        );

        const cart = getCartFromCache();
        const optimisticCart = mergeCart( mergeStrategy )( cart, queuedLineItemMutations );
        console.log( '[optimisticResponse]', toLineItemsPayload( selection, optimisticCart, queuedLineItemMutations ) );

        let mutationResult;

        setTimeout( () => {
            Cart.trigger( selection, lineItems );
        }, 10 );

        try {
            const mutationRequest = client.mutate( {
                mutation,
                variables: toLineItemInput( lineItems, options ),
                optimisticResponse: toLineItemsPayload( selection, optimisticCart, queuedLineItemMutations ),
                update: syncCart( selection, GetCart, queuedLineItemMutations )
            } );

            if ( typeof options.onOptimistic === 'function' ) {
                runCartSideEffect(
                    'onOptimistic',
                    () => options.onOptimistic( optimisticCart )
                );
            }

            const resp = await mutationRequest;

            mutationResult = resp && resp.data && resp.data[selection];
            if ( !mutationResult || !mutationResult.cart ) {
                throw new Error( `Missing confirmed cart for ${selection}` );
            }
        }
        catch ( err ) {
            if ( propagateErrors ) {
                throw err;
            }
            return;
        }
        finally {
            mutationsQueue.remove( selection, lineItems );
        }

        const confirmedCart = mutationResult.cart;
        const cartScope = confirmedCart.orderGuid;
        let analyticsLineItems = lineItems;

        if ( selection === ADD_LINE_ITEMS ) {
            analyticsLineItems = getConfirmedAdditions(
                lineItems,
                mutationResult.changedLineItems
            );
            analyticsLineItems = runCartSideEffect(
                'applyProductSelectionAttribution',
                () => applyProductSelectionAttribution( analyticsLineItems, undefined, cartScope ),
                analyticsLineItems
            );
            runCartSideEffect(
                'rememberCartAttributions',
                () => rememberCartAttributions( analyticsLineItems, undefined, cartScope )
            );
            runCartSideEffect(
                'removeProductSelections',
                () => removeProductSelections( analyticsLineItems, undefined, cartScope )
            );
        }

        runCartSideEffect(
            'trackCartChange',
            () => trackCartChange( selection, analyticsLineItems )
        );

        if ( selection === DELETE_LINE_ITEMS ) {
            runCartSideEffect(
                'removeCartAttributions',
                () => removeCartAttributions( lineItems, undefined, cartScope )
            );
        }

        // trigger cart change events: e.g. one of ["lineItemsAdded", "lineItemsUpdated", "lineItemsDeleted"]
        setTimeout( () => {
            Cart.trigger( eventMap[selection], lineItems );
            Cart.trigger( 'cartChange', lineItems );
        }, 10 );

        console.log( 'updated state', confirmedCart );
        return confirmedCart;
    };

const clearCart = async () => {
    const cart = getCartFromCache();
    const mutation = createLineItemMutation(
        DeleteLineItems,
        DELETE_LINE_ITEMS,
        // force empty cart
        () => ([])
    )
    return mutation( cart.lineItems );
}

const processOrder = async ( state ) => {
    const cart = state
        ? {...getCartFromCache(), ...state}
        : getCartFromCache();

    const orderInput = toBookOrderInput( cart );
    const resp = await client.mutate( {
        mutation: BookOrder,
        variables: {input: orderInput},
        // apollo-link-timeout skips requests with a computed timeout <= 0.
        context: {timeout: DISABLE_TIMEOUT}
    } );

    return resp.data.bookOrder;
}

const submitPunchOutOrder = ( currentOrderGuid, location = window.location ) => {
    if ( !currentOrderGuid ) {
        throw new Error( `"currentOrderGuid" is a required parameter, received "${currentOrderGuid}" instead` );
    }

    location.assign( `/punchout/SubmitOrder/${currentOrderGuid}` );
}

const clearPendingOrderServerContext = async ( request = window.fetch ) => {
    const response = await request( '/checkout/clear-edit-context', {
        method: 'POST',
        credentials: 'same-origin'
    } );

    if ( !response.ok ) {
        throw new Error( 'Unable to clear pending order edit context.' );
    }

    return response;
};

const toBookOrderInput = ( state ) => {
    const emptyFields = [];
    const fieldsToCheck = [
        'cartId',
        'shippingAddressId',
        'shippingMethod'
    ];

    if ( !state.approvalRequired && !state.skipPaymentValidation ) {
        fieldsToCheck.push( 'billingAddressId', 'paymentId' );
    }

    const payload = {
        billingAddressId: state.payment?.billingAddressId,
        cartId: state.id,
        paymentId: state.payment?.paymentId,
        poNumber: state.PONumber,
        shippingAddressId: state.shippingAddress?.id,
        shippingMethod: state.shippingMethod?.id
    };

    fieldsToCheck.forEach( field => {
        if ( isEmptyOrNil( payload[field] ) ) {
            emptyFields.push( field );
        }
    } );

    if ( emptyFields.length > 0 ) {
        throw new Error( `Invalid state object. The following fields are empty: ${emptyFields.join( ', ' )}` );
    }

    return payload;
};

export default {
    getCart,
    priceCart,
    getCartFromCache,
    readCartFromCache,
    clearCartCache,
    getCartStatus,
    subscribeCartStatus,
    setCartStatus,
    getAvailableShippingMethods,
    updateShippingAddress,
    addPromotion,
    removePromotion,
    updateShippingMethod,
    clearCart,
    processOrder,
    submitPunchOutOrder,
    clearPendingOrderServerContext,
    addLineItems: createLineItemMutation( AddLineItems, ADD_LINE_ITEMS, cartAdditionsStrategy, true ),
    updateLineItems: createLineItemMutation( UpdateLineItems, UPDATE_LINE_ITEMS, cartUpdatesStrategy ),
    deleteLineItems: createLineItemMutation( DeleteLineItems, DELETE_LINE_ITEMS, cartDeletionsStrategy )
};
